"use client";

import type { HTMLInputTypeAttribute } from "react";
import type { EditableIndividualProfile } from "@/lib/cat/individual-profile.types";
import type { MdmContactUse } from "@/lib/cat/individual-profile.types";
import type { PhoneContact } from "@/lib/cat/individual-profile.types";
import { formatPhoneWithUseLabel } from "@/lib/cat/mdm-map-contacts";
import {
  INVALID_EMAIL_MESSAGE,
  REQUIRED_FIELD_MESSAGE,
  hasAnyAddressFieldFilled,
  validatePartyProfileFields,
} from "@/lib/cat/party-profile-validation";
import {
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

export type ProfileFieldsValue = Pick<
  EditableIndividualProfile,
  | "email"
  | "addressLine"
  | "addressFloorApartment"
  | "addressBuildingResidence"
  | "postBox"
  | "postalCode"
  | "city"
  | "fixedPhone"
  | "mobilePhone"
>;

type LockedFields = Partial<Record<keyof ProfileFieldsValue, boolean>>;

export const INSEE_LOCKED_FIELD_HINT = "Lecture seule — INSEE";

function RequiredMark() {
  return <span className="text-destructive"> *</span>;
}

type PartyProfileFieldsProps = {
  idPrefix: string;
  value: ProfileFieldsValue;
  onChange: (value: ProfileFieldsValue) => void;
  readOnly?: boolean;
  lockedFields?: LockedFields;
  lockedFieldHint?: string;
  emailLabel?: string;
  showEmail?: boolean;
  showValidation?: boolean;
  requireEmail?: boolean;
  requireEmailOrPhone?: boolean;
};

export function ProfileFieldLabel({
  htmlFor,
  label,
  locked,
  lockedHint = INSEE_LOCKED_FIELD_HINT,
  required,
  requiredHint,
}: {
  htmlFor: string;
  label: string;
  locked?: boolean;
  lockedHint?: string;
  required?: boolean;
  requiredHint?: string;
}) {
  return (
    <FieldLabel
      htmlFor={htmlFor}
      className="flex w-full flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5"
    >
      <span>
        {label}
        {required ? <RequiredMark /> : null}
        {requiredHint ? (
          <span className="text-muted-foreground ml-1 text-xs font-normal">
            {requiredHint}
          </span>
        ) : null}
      </span>
      {locked ? (
        <span className="text-muted-foreground text-xs font-normal">
          {lockedHint}
        </span>
      ) : null}
    </FieldLabel>
  );
}

export function ProfileTextInput({
  id,
  locked,
  type = "text",
  value,
  onChange,
  invalid,
  autoComplete,
  name,
}: {
  id: string;
  locked: boolean;
  type?: HTMLInputTypeAttribute;
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
  autoComplete?: string;
  name?: string;
}) {
  return (
    <Input
      id={id}
      name={name}
      type={type}
      autoComplete={autoComplete}
      readOnly={locked}
      aria-readonly={locked || undefined}
      aria-invalid={invalid || undefined}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function patchPhoneValue(
  current: PhoneContact,
  nextValue: string,
): PhoneContact {
  const trimmed = nextValue.trim();
  if (!trimmed) {
    return { value: nextValue, use: "unknown" };
  }
  if (!current.value.trim()) {
    return { value: nextValue, use: "unknown" };
  }
  return { ...current, value: nextValue };
}

function PhoneUseCheckboxes({
  idPrefix,
  use,
  onChange,
  disabled,
  phoneFilled,
}: {
  idPrefix: string;
  use: MdmContactUse;
  onChange: (use: MdmContactUse) => void;
  disabled?: boolean;
  phoneFilled: boolean;
}) {
  if (!phoneFilled) {
    return <p className="text-muted-foreground text-sm">—</p>;
  }

  return (
    <div className="flex flex-wrap gap-4 pt-1">
      <div className="flex items-center gap-2">
        <Checkbox
          id={`${idPrefix}-use-home`}
          checked={use === "home"}
          disabled={disabled}
          onCheckedChange={(checked) =>
            onChange(checked === true ? "home" : "unknown")
          }
        />
        <label htmlFor={`${idPrefix}-use-home`} className="text-sm font-normal">
          Personnel
        </label>
      </div>
      <div className="flex items-center gap-2">
        <Checkbox
          id={`${idPrefix}-use-work`}
          checked={use === "work"}
          disabled={disabled}
          onCheckedChange={(checked) =>
            onChange(checked === true ? "work" : "unknown")
          }
        />
        <label htmlFor={`${idPrefix}-use-work`} className="text-sm font-normal">
          Professionnel
        </label>
      </div>
    </div>
  );
}

function isLocked(
  field: keyof ProfileFieldsValue,
  lockedFields: LockedFields | undefined,
  readOnly: boolean,
): boolean {
  if (readOnly) return true;
  return Boolean(lockedFields?.[field]);
}

export function PartyProfileFields({
  idPrefix,
  value,
  onChange,
  readOnly = false,
  lockedFields,
  lockedFieldHint = INSEE_LOCKED_FIELD_HINT,
  emailLabel = "Email",
  showEmail = true,
  showValidation = false,
  requireEmail = false,
  requireEmailOrPhone = false,
}: PartyProfileFieldsProps) {
  const patch = (partial: Partial<ProfileFieldsValue>) =>
    onChange({ ...value, ...partial });

  const fieldLocked = (field: keyof ProfileFieldsValue) =>
    isLocked(field, lockedFields, readOnly);

  const fieldErrors = showValidation
    ? validatePartyProfileFields(value, {
        requireEmail: requireEmailOrPhone ? false : requireEmail,
        requireEmailOrPhone,
      })
    : {};

  const contactRequired = requireEmailOrPhone;
  const addressRequired = hasAnyAddressFieldFilled(value);

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {showEmail ? (
        <Field data-invalid={fieldErrors.email ? true : undefined}>
          <ProfileFieldLabel
            htmlFor={`${idPrefix}-email`}
            label={emailLabel}
            locked={fieldLocked("email")}
            lockedHint={lockedFieldHint}
            required={requireEmail || contactRequired}
            requiredHint={
              contactRequired ? "ou téléphone (fixe ou mobile)" : undefined
            }
          />
          <FieldContent>
            <ProfileTextInput
              id={`${idPrefix}-email`}
              type="email"
              name={`${idPrefix}-email`}
              autoComplete="email"
              locked={fieldLocked("email")}
              invalid={Boolean(fieldErrors.email)}
              value={value.email}
              onChange={(email) => patch({ email })}
            />
            {fieldErrors.email ? (
              <FieldError>{fieldErrors.email}</FieldError>
            ) : null}
          </FieldContent>
        </Field>
      ) : null}

      <Field className={showEmail ? undefined : "md:col-span-2"} />

      <Field data-invalid={fieldErrors.fixedPhone ? true : undefined}>
        <ProfileFieldLabel
          htmlFor={`${idPrefix}-fixed-phone`}
          label="Téléphone fixe"
          locked={fieldLocked("fixedPhone")}
          lockedHint={lockedFieldHint}
          required={contactRequired}
          requiredHint={contactRequired ? "ou email" : undefined}
        />
        <FieldContent>
          <ProfileTextInput
            id={`${idPrefix}-fixed-phone`}
            name={`${idPrefix}-fixed-phone`}
            autoComplete="tel"
            locked={fieldLocked("fixedPhone")}
            invalid={Boolean(fieldErrors.fixedPhone)}
            value={value.fixedPhone.value}
            onChange={(next) =>
              patch({
                fixedPhone: patchPhoneValue(value.fixedPhone, next),
              })
            }
          />
          {fieldErrors.fixedPhone ? (
            <FieldError>{fieldErrors.fixedPhone}</FieldError>
          ) : null}
        </FieldContent>
      </Field>
      <Field>
        <FieldLabel>Usage (fixe)</FieldLabel>
        <FieldContent>
          <PhoneUseCheckboxes
            idPrefix={`${idPrefix}-fixed`}
            use={value.fixedPhone.use}
            phoneFilled={Boolean(value.fixedPhone.value.trim())}
            disabled={fieldLocked("fixedPhone")}
            onChange={(use) =>
              patch({ fixedPhone: { ...value.fixedPhone, use } })
            }
          />
        </FieldContent>
      </Field>

      <Field data-invalid={fieldErrors.mobilePhone ? true : undefined}>
        <ProfileFieldLabel
          htmlFor={`${idPrefix}-mobile-phone`}
          label="Téléphone mobile"
          locked={fieldLocked("mobilePhone")}
          lockedHint={lockedFieldHint}
          required={contactRequired}
          requiredHint={contactRequired ? "ou email" : undefined}
        />
        <FieldContent>
          <ProfileTextInput
            id={`${idPrefix}-mobile-phone`}
            name={`${idPrefix}-mobile-phone`}
            autoComplete="tel"
            locked={fieldLocked("mobilePhone")}
            invalid={Boolean(fieldErrors.mobilePhone)}
            value={value.mobilePhone.value}
            onChange={(next) =>
              patch({
                mobilePhone: patchPhoneValue(value.mobilePhone, next),
              })
            }
          />
          {fieldErrors.mobilePhone ? (
            <FieldError>{fieldErrors.mobilePhone}</FieldError>
          ) : null}
        </FieldContent>
      </Field>
      <Field>
        <FieldLabel>Usage (mobile)</FieldLabel>
        <FieldContent>
          <PhoneUseCheckboxes
            idPrefix={`${idPrefix}-mobile`}
            use={value.mobilePhone.use}
            phoneFilled={Boolean(value.mobilePhone.value.trim())}
            disabled={fieldLocked("mobilePhone")}
            onChange={(use) =>
              patch({ mobilePhone: { ...value.mobilePhone, use } })
            }
          />
        </FieldContent>
      </Field>

      <Field
        className="md:col-span-2"
        data-invalid={fieldErrors.addressLine ? true : undefined}
      >
        <ProfileFieldLabel
          htmlFor={`${idPrefix}-address-line`}
          label="Numéro et voie"
          locked={fieldLocked("addressLine")}
          lockedHint={lockedFieldHint}
          required={addressRequired}
        />
        <FieldContent>
          <ProfileTextInput
            id={`${idPrefix}-address-line`}
            locked={fieldLocked("addressLine")}
            invalid={Boolean(fieldErrors.addressLine)}
            value={value.addressLine}
            onChange={(addressLine) => patch({ addressLine })}
          />
          {fieldErrors.addressLine ? (
            <FieldError>{fieldErrors.addressLine}</FieldError>
          ) : null}
        </FieldContent>
      </Field>

      <div className="grid grid-cols-1 gap-4 md:col-span-2 md:grid-cols-3">
        <Field>
          <ProfileFieldLabel
            htmlFor={`${idPrefix}-floor`}
            label="Étage, porte, appartement"
            locked={fieldLocked("addressFloorApartment")}
            lockedHint={lockedFieldHint}
          />
          <FieldContent>
            <ProfileTextInput
              id={`${idPrefix}-floor`}
              locked={fieldLocked("addressFloorApartment")}
              value={value.addressFloorApartment}
              onChange={(addressFloorApartment) =>
                patch({ addressFloorApartment })
              }
            />
          </FieldContent>
        </Field>
        <Field>
          <ProfileFieldLabel
            htmlFor={`${idPrefix}-building`}
            label="Escalier, bâtiment, résidence"
            locked={fieldLocked("addressBuildingResidence")}
            lockedHint={lockedFieldHint}
          />
          <FieldContent>
            <ProfileTextInput
              id={`${idPrefix}-building`}
              locked={fieldLocked("addressBuildingResidence")}
              value={value.addressBuildingResidence}
              onChange={(addressBuildingResidence) =>
                patch({ addressBuildingResidence })
              }
            />
          </FieldContent>
        </Field>
        <Field>
          <ProfileFieldLabel
            htmlFor={`${idPrefix}-postbox`}
            label="Boîte postale"
            locked={fieldLocked("postBox")}
            lockedHint={lockedFieldHint}
          />
          <FieldContent>
            <ProfileTextInput
              id={`${idPrefix}-postbox`}
              name={`${idPrefix}-post-box`}
              autoComplete="off"
              locked={fieldLocked("postBox")}
              value={value.postBox}
              onChange={(postBox) => patch({ postBox })}
            />
          </FieldContent>
        </Field>
      </div>

      <Field data-invalid={fieldErrors.postalCode ? true : undefined}>
        <ProfileFieldLabel
          htmlFor={`${idPrefix}-postal`}
          label="Code postal"
          locked={fieldLocked("postalCode")}
          lockedHint={lockedFieldHint}
          required={addressRequired}
        />
        <FieldContent>
          <ProfileTextInput
            id={`${idPrefix}-postal`}
            name={`${idPrefix}-postal-code`}
            autoComplete="postal-code"
            locked={fieldLocked("postalCode")}
            invalid={Boolean(fieldErrors.postalCode)}
            value={value.postalCode}
            onChange={(postalCode) => patch({ postalCode })}
          />
          {fieldErrors.postalCode ? (
            <FieldError>{fieldErrors.postalCode}</FieldError>
          ) : null}
        </FieldContent>
      </Field>
      <Field data-invalid={fieldErrors.city ? true : undefined}>
        <ProfileFieldLabel
          htmlFor={`${idPrefix}-city`}
          label="Ville"
          locked={fieldLocked("city")}
          lockedHint={lockedFieldHint}
          required={addressRequired}
        />
        <FieldContent>
          <ProfileTextInput
            id={`${idPrefix}-city`}
            locked={fieldLocked("city")}
            invalid={Boolean(fieldErrors.city)}
            value={value.city}
            onChange={(city) => patch({ city })}
          />
          {fieldErrors.city ? (
            <FieldError>{fieldErrors.city}</FieldError>
          ) : null}
        </FieldContent>
      </Field>
    </div>
  );
}

const profileDisplayValueClassName = "min-w-0 break-words text-sm";
const profileDisplayGridClassName =
  "grid grid-cols-1 gap-3 md:grid-cols-2 [&>[data-slot=field]]:min-w-0";

export function ProfileReadOnlyLines({
  lines,
}: {
  lines: Array<{ label: string; value: string }>;
}) {
  if (lines.length === 0) {
    return <p className="text-muted-foreground text-sm">—</p>;
  }
  return (
    <div className="space-y-2">
      {lines.map((line) => (
        <div key={line.label} className="min-w-0">
          <p className="text-muted-foreground text-xs">{line.label}</p>
          <p className={profileDisplayValueClassName}>{line.value}</p>
        </div>
      ))}
    </div>
  );
}

export function buildProfileDisplayLines(
  value: ProfileFieldsValue & {
    companyDenomination?: string;
    apeCode?: string;
  },
  options?: {
    includeIdentity?: boolean;
    emailBrandLabel?: string;
  },
): Array<{ label: string; value: string }> {
  const lines: Array<{ label: string; value: string }> = [];

  if (options?.includeIdentity && value.companyDenomination?.trim()) {
    lines.push({
      label: "Raison sociale",
      value: value.companyDenomination.trim(),
    });
  }
  if (options?.includeIdentity && value.apeCode?.trim()) {
    lines.push({ label: "Code APE", value: value.apeCode.trim() });
  }
  if (value.addressLine.trim()) {
    lines.push({ label: "Voie", value: value.addressLine.trim() });
  }
  if (value.addressFloorApartment.trim()) {
    lines.push({
      label: "Étage, porte, appartement",
      value: value.addressFloorApartment.trim(),
    });
  }
  if (value.addressBuildingResidence.trim()) {
    lines.push({
      label: "Escalier, bâtiment, résidence",
      value: value.addressBuildingResidence.trim(),
    });
  }
  if (value.postBox.trim()) {
    lines.push({ label: "Boîte postale", value: value.postBox.trim() });
  }
  const locality = [value.postalCode.trim(), value.city.trim()]
    .filter(Boolean)
    .join(" ");
  if (locality) {
    lines.push({ label: "Code postal / ville", value: locality });
  }
  if (value.email.trim()) {
    const label = options?.emailBrandLabel
      ? `Email (${options.emailBrandLabel})`
      : "Email";
    lines.push({ label, value: value.email.trim() });
  }
  if (value.fixedPhone.value.trim()) {
    lines.push({
      label: "Téléphone fixe",
      value: formatPhoneWithUseLabel(
        value.fixedPhone.value,
        value.fixedPhone.use,
      ),
    });
  }
  if (value.mobilePhone.value.trim()) {
    lines.push({
      label: "Téléphone mobile",
      value: formatPhoneWithUseLabel(
        value.mobilePhone.value,
        value.mobilePhone.use,
      ),
    });
  }

  return lines;
}

export {
  INVALID_EMAIL_MESSAGE,
  profileDisplayGridClassName,
  profileDisplayValueClassName,
  REQUIRED_FIELD_MESSAGE,
};
